import React, { useCallback, useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Breadcrumb from 'shared/components/Breadcrumb';
import Layout from 'containers/Layout';
import LogIn from 'containers/LogIn';
import Home from 'containers/Home';
import Documentations from 'containers/Documentations';
import Documentation from 'containers/Documentation';
import Notifications from 'containers/Notifications';
import Notification from 'containers/Notification';
import ForgotPassword from 'containers/ForgotPassword';
import ResetPassword from 'containers/ResetPassword';
import NotificationReview from 'containers/NotificationReview';
import GeneralCustomizer from 'containers/GeneralCustomizer';
import Clients from 'containers/Clients';
import SelectedFolderDocument from 'containers/SelectedFolderDocument';
import Calendar from 'containers/Calendar';
import ClientManagement from 'containers/ClientManagement';
import ResultsSearchAllSite from 'containers/ResultsSearchAllSite';
import AutoLogin from 'containers/AutoLogin';
import AutoDownload from 'containers/AutoDownload';
import AutoDownloadContact from 'containers/AutoDownloadContact';
import ClientAccessManager from 'containers/ClientAccessManager';

import Settings from 'containers/Settings';
import withLayoutAuth from 'hoc/WithLayoutAut';
import MainSpinner from 'shared/components/MainSpinner';
import { emptyString } from 'shared/utils';

import useApi from 'hooks/useApi';
import { changeTheme, setClientLink } from 'redux/actions/themeActions';
import { setIdCustomer } from 'redux/actions/authActions';

import MainWrapper from './MainWrapper';
import PrivateRoute from './PrivateRoute';

const wrappedRoutes = () => {
  const { pathname } = useLocation();
  const layout = useSelector((state) => state.layout);
  const { cookies } = layout;
  return (
    <div>
      <div className={!cookies ? 'cooking-display-wrap container__wrap' : 'container__wrap'}>
        {pathname !== '/home' && <Breadcrumb />}
        <PrivateRoute exact path="/home" component={Home} />
        <PrivateRoute exact path="/documentations" component={Documentations} />
        <PrivateRoute exact path="/documentation" component={Documentation} />
        <PrivateRoute exact path="/notifications" component={Notifications} />
        <PrivateRoute exact path="/new_notificacion" onlyCustomer component={Notification} />
        <PrivateRoute exact path="/edit_notificacion" component={Notification} />
        <PrivateRoute exact path="/notificacion_details" component={NotificationReview} />
        <PrivateRoute exact path="/clients" component={Clients} onlyCustomer />
        <PrivateRoute exact path="/clients/:action" component={ClientManagement} onlyCustomer />
        <PrivateRoute exact path="/clients/:action/:id" component={ClientManagement} onlyCustomer />
        <PrivateRoute exact path="/Calendar" component={Calendar} onlyCustomer />
        <PrivateRoute exact path="/settings" component={Settings} onlyCustomer />
        <PrivateRoute exact path="/download_document" component={AutoDownload} />
        <PrivateRoute
          exact
          path="/searchAll/results/:searchText"
          component={ResultsSearchAllSite}
        />
        <PrivateRoute exact path="/searchAll/results" component={ResultsSearchAllSite} />
        <PrivateRoute exact path="/add_documentation" component={SelectedFolderDocument} />
        <PrivateRoute exact path="/access_manager" component={ClientAccessManager} onlyCustomer />
      </div>
      <Layout />
    </div>
  );
};

const Router = () => {
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme);
  const { title, favicon } = theme;
  const { isCustomer } = useSelector((st) => st.auth);
  const layout = useSelector((state) => state.layout);
  const { cookies } = layout;
  document.title = title !== '' && title ? title : 'Customer area';
  const favIconEl = document.getElementById('favicon');
  if (favicon && !emptyString(favicon)) {
    favIconEl.href = `${favicon}`;
  }
  const [{ loading: configCustomerLoading }, getConfigCustomerByDomain] = customAreaApi(
    {
      url: `${window.API_V1}/cus/customers/search`,
      method: 'POST',
    },
    { manual: true }
  );
  const [{ loading: configNoCustomerLoading }, getConfigNoCustomerByDomain] = customAreaApi(
    {
      url: `${window.API_V1}/cus/domains/configuration`,
      method: 'POST',
    },
    { manual: true }
  );
  const loadCustomerConfigs = useCallback(async () => {
    try {
      const {
        data: { data, errors },
      } = await getConfigCustomerByDomain({
        data: {
          filters: [
            {
              model: 'Domain',
              field: 'Url',
              filter: [window.location.origin],
            },
          ],
          reduce: {
            limit: 1,
            skip: 0,
          },
        },
      });
      if (errors.length) {
        throw new Error('error throw by endpoint get config');
      }
      const customerData = data?.[0];
      if (!customerData?.config) {
        throw new Error('empty customer config response');
      }
      const existClientLink = customerData.relations?.some((x) => x === 'clientlink');
      dispatch(setClientLink(existClientLink));
      dispatch(changeTheme(customerData.config));
    } catch (ex) {
      console.error('getting config error: ', ex);
    }
  }, []);

  const loadNoCustomerConfigs = useCallback(async () => {
    try {
      const {
        data: { data, errors },
      } = await getConfigNoCustomerByDomain({
        data: {
          domain: window.location.origin,
        },
      });
      if (errors.length) {
        throw new Error('error throw by endpoint get config');
      }
      if (!data?.config) {
        throw new Error('empty no-customer config response');
      }
      dispatch(changeTheme(data.config));
      dispatch(setIdCustomer(data.clientNavision));
    } catch (ex) {
      console.error('getting config error: ', ex);
    }
  }, []);
  useEffect(() => {
    if (isCustomer) {
      loadCustomerConfigs();
    } else {
      loadNoCustomerConfigs();
    }
  }, [loadCustomerConfigs, loadNoCustomerConfigs]);
  if (configCustomerLoading || configNoCustomerLoading) {
    return (
      <MainWrapper>
        <main className={!cookies ? 'cooking-display' : ''}>
          <MainSpinner />
        </main>
      </MainWrapper>
    );
  }
  return (
    <MainWrapper>
      <main className={!cookies ? 'cooking-display' : ''}>
        <Switch>
          <Route exact path="/" component={withLayoutAuth(LogIn)} />
          <Route exact path="/log_in" component={withLayoutAuth(LogIn)} />
          <Route exact path="/forgot_password" component={withLayoutAuth(ForgotPassword)} />
          <Route exact path="/reset_password" component={withLayoutAuth(ResetPassword)} />
          <Route exact path="/download_document_contact" component={AutoDownloadContact} />
          <PrivateRoute
            exact
            path="/general_customization"
            component={GeneralCustomizer}
            onlyCustomer
          />
          <PrivateRoute
            exact
            path="/general_customization/:action"
            component={GeneralCustomizer}
            onlyCustomer
          />
          <Route exact path="/access/:token" component={withLayoutAuth(AutoLogin)} />
          <Route path="/" component={wrappedRoutes} />
        </Switch>
      </main>
    </MainWrapper>
  );
};

export default Router;
